import { M_PER_DEG_LAT, metresPerDegLng } from '@/core/utils';
import {
  altimeterFromStationPressure,
  densityAltitude,
  stationPressureFromAltimeter,
} from './densityAltitude';
import { PREFERRED_NETWORKS } from './rank';
import type { Station } from './types';
import { decodeWeatherCode } from './weatherCodes';

// Synoptic Weather API, latest observations: https://docs.synopticdata.com/services/latest

export const SYNOPTIC_LATEST_URL = 'https://api.synopticdata.com/v2/stations/latest';

const VARS = [
  'wind_speed',
  'wind_gust',
  'wind_direction',
  'air_temp',
  'dew_point_temperature',
  'pressure',
  'altimeter',
  'weather_cond_code',
  'precip_accum_one_hour',
];

/** Observations older than this are not shown, minutes. */
export const MAX_AGE_MINUTES = 90;

export interface LatestQuery {
  token: string;
  bounds: { w: number; s: number; e: number; n: number };
  /** Size of the area on screen, px, and the wanted distance between stations: the API thins them out. */
  width: number;
  height: number;
  spacing: number;
}

export function latestUrl({ token, bounds: b, width, height, spacing }: LatestQuery): string {
  const params = new URLSearchParams({
    token,
    bbox: [b.w, b.s, b.e, b.n].map((v) => v.toFixed(4)).join(','),
    width: String(Math.round(width)),
    height: String(Math.round(height)),
    spacing: String(Math.round(spacing)),
    networkimportance: PREFERRED_NETWORKS.join(','),
    vars: VARS.join(','),
    within: String(MAX_AGE_MINUTES),
    status: 'active',
    units: 'metric,speed|mps,temp|C,pres|pa,alti|pa,precip|mm',
  });
  return `${SYNOPTIC_LATEST_URL}?${params}`;
}

interface Observation {
  date_time?: string;
  value?: unknown;
}

export interface SynopticStation {
  STID: string;
  NAME?: string;
  MNET_ID?: string | number;
  LATITUDE?: string | number;
  LONGITUDE?: string | number;
  ELEVATION?: string | number | null;
  UNITS?: { elevation?: string };
  OBSERVATIONS?: Record<string, Observation>;
}

export interface LatestResponse {
  STATION?: SynopticStation[];
  SUMMARY?: { RESPONSE_CODE?: number; RESPONSE_MESSAGE?: string };
}

/** The API answered with an error (bad token, rule violation, …). */
export class SynopticApiError extends Error {
  readonly code: number;
  constructor(code: number, message: string) {
    super(`Synoptic API ${code}: ${message}`);
    this.name = 'SynopticApiError';
    this.code = code;
  }
}

/**
 * Gusts and present weather are only reported while they happen, so the latest one may belong to an
 * earlier report; such values only count when observed this close to the wind, ms.
 */
const SAME_REPORT_MS = 20 * 60_000;

/** Response code of an authentication failure (bad, disabled or origin-restricted token). */
export const AUTH_FAILURE = 200;

const toNumber = (v: unknown): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * Value of a variable. Sensors are numbered (`air_temp_value_1`, `_value_2`, …) and derived values carry
 * a `d` (`pressure_value_1d`); the lowest-numbered sensor wins, measured over derived.
 */
function observation(obs: Record<string, Observation>, name: string): { value: number; time: number } | null {
  const pattern = new RegExp(`^${name}_value_(\\d+)(d?)$`);
  let best: { order: number; value: number; time: number } | null = null;
  for (const [key, o] of Object.entries(obs)) {
    const m = pattern.exec(key);
    const value = toNumber(o.value);
    const time = Date.parse(o.date_time ?? '');
    if (!m || value === null || Number.isNaN(time)) continue;
    const order = Number(m[1]) * 2 + (m[2] ? 1 : 0);
    if (!best || order < best.order) best = { order, value, time };
  }
  return best;
}

export function parseStation(raw: SynopticStation): Station | null {
  const lat = toNumber(raw.LATITUDE);
  const lng = toNumber(raw.LONGITUDE);
  const obs = raw.OBSERVATIONS ?? {};
  const wind = observation(obs, 'wind_speed');
  if (lat === null || lng === null || !wind) return null;

  const value = (name: string) => observation(obs, name)?.value ?? null;
  const current = (name: string) => {
    const o = observation(obs, name);
    return o && Math.abs(o.time - wind.time) <= SAME_REPORT_MS ? o.value : null;
  };
  const elevationRaw = toNumber(raw.ELEVATION);
  // Station elevation comes in feet unless the response says otherwise.
  const elevation =
    elevationRaw === null ? null : raw.UNITS?.elevation === 'm' ? elevationRaw : elevationRaw * 0.3048;

  const airTemp = value('air_temp');
  const dewPoint = value('dew_point_temperature');
  const reportedPressure = value('pressure');
  const altimeter =
    value('altimeter') ??
    (reportedPressure !== null && elevation !== null
      ? altimeterFromStationPressure(reportedPressure, elevation)
      : null);
  const pressure =
    reportedPressure ??
    (altimeter !== null && elevation !== null ? stationPressureFromAltimeter(altimeter, elevation) : null);
  const weatherCode = current('weather_cond_code');
  const gust = current('wind_gust');

  return {
    id: raw.STID,
    name: raw.NAME?.trim() || raw.STID,
    network: String(raw.MNET_ID ?? ''),
    lat,
    lng,
    elevation,
    time: wind.time,
    windSpeed: Math.max(0, wind.value),
    // A gust below the mean speed is a stale reading from another report.
    windGust: gust !== null && gust >= wind.value ? gust : null,
    windDirection: value('wind_direction'),
    airTemp,
    dewPoint,
    altimeter,
    densityAltitude:
      pressure !== null && airTemp !== null ? densityAltitude(pressure, airTemp, dewPoint) : null,
    densityAltitudeEstimated: false,
    weather: weatherCode === null ? null : decodeWeatherCode(weatherCode),
    precip1h: current('precip_accum_one_hour'),
  };
}

/** Altimeter settings of stations farther away than this are not borrowed, m. */
const ALTIMETER_RANGE = 150_000;
const STANDARD_ALTIMETER = 101325;

/**
 * Many stations (fire weather, road weather) measure temperature but not pressure. Pressure varies
 * smoothly over a region, so they borrow the altimeter setting of the nearest station reporting one, as
 * pilots do with the nearest airport's (the standard atmosphere if none is in range).
 */
export function estimateDensityAltitudes(stations: Station[]): Station[] {
  const sources = stations.filter((s) => s.altimeter !== null);
  return stations.map((s) => {
    if (s.densityAltitude !== null || s.airTemp === null || s.elevation === null) return s;
    const kx = metresPerDegLng(s.lat);
    let altimeter = STANDARD_ALTIMETER;
    let best = ALTIMETER_RANGE ** 2;
    for (const q of sources) {
      const d2 = ((q.lng - s.lng) * kx) ** 2 + ((q.lat - s.lat) * M_PER_DEG_LAT) ** 2;
      if (d2 < best) [best, altimeter] = [d2, q.altimeter!];
    }
    const pressure = stationPressureFromAltimeter(altimeter, s.elevation);
    return {
      ...s,
      densityAltitude: densityAltitude(pressure, s.airTemp, s.dewPoint),
      densityAltitudeEstimated: true,
    };
  });
}

/** Stations with a wind observation. Throws SynopticApiError when the API reports an error. */
export function parseLatest(res: LatestResponse): Station[] {
  const code = res.SUMMARY?.RESPONSE_CODE ?? 1;
  if (code === 2) return []; // zero results
  if (code !== 1) throw new SynopticApiError(code, res.SUMMARY?.RESPONSE_MESSAGE ?? '');
  return estimateDensityAltitudes((res.STATION ?? []).flatMap((raw) => parseStation(raw) ?? []));
}
